import { useMemo } from "react";
import { parseISO } from "date-fns";
import { getAllPricePoints } from "../utils";
import type { Event, EventPricePoint } from "../types";

export interface ProcessedPricePoint {
  pp: EventPricePoint;
  isFuture: boolean;
}

export function useEventPricePoints(event: Event) {
  return useMemo(() => {
    const { available: availablePricePoints, future: futurePricePoints } =
      event.isCancelled
        ? { available: [], future: [] }
        : getAllPricePoints(
            event.pricePoints || [],
            event.bookingClosesAt ?? null,
            event.startsAt,
          );

    const bookingEnd = event.bookingClosesAt
      ? new Date(event.bookingClosesAt)
      : event.startsAt
        ? new Date(event.startsAt)
        : new Date();

    const allPricePoints = [...availablePricePoints, ...futurePricePoints];

    const hasAnyNotes = allPricePoints.some((pricePoint: EventPricePoint) => {
      if (!pricePoint.availableUntil) return false;
      return new Date(pricePoint.availableUntil) < bookingEnd;
    });

    const hasFuturePricePoints = futurePricePoints.length > 0;

    const allPricePointsWithStatus: ProcessedPricePoint[] = [
      ...availablePricePoints.map((pp) => ({ pp, isFuture: false })),
      ...futurePricePoints.map((pp) => ({ pp, isFuture: true })),
    ];

    const groupedByLabel = new Map<string, ProcessedPricePoint[]>();
    allPricePointsWithStatus.forEach((item) => {
      const label = item.pp.label;
      if (!groupedByLabel.has(label)) {
        groupedByLabel.set(label, []);
      }
      groupedByLabel.get(label)!.push(item);
    });

    groupedByLabel.forEach((group) => {
      group.sort((a, b) => {
        const aFrom = a.pp.availableFrom
          ? parseISO(a.pp.availableFrom).getTime()
          : 0;
        const bFrom = b.pp.availableFrom
          ? parseISO(b.pp.availableFrom).getTime()
          : 0;
        return aFrom - bFrom;
      });
    });

    return {
      availablePricePoints,
      futurePricePoints,
      groupedByLabel,
      hasAnyNotes,
      hasFuturePricePoints,
      bookingEnd,
    };
  }, [
    event.isCancelled,
    event.pricePoints,
    event.bookingClosesAt,
    event.startsAt,
  ]);
}






